import React, { ReactNode, useEffect, useState, useRef } from 'react';
import styles from './LegViolation.module.scss';
import Icon from '../../common/Icon/Icon';
import { useAppSelector } from '../../../redux/hooks';
import Tooltip from '../../common/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { getCustomShortMonthLabel, useABOProfileNavigation } from '../../../utils/common';
import MyAccountsLoader from '../../common/Loaders/MyAccountsLoader/MyAccountsLoader';
import ErrorInPageScreen from '../../common/ErrorInPageScreen/ErrorInPageScreen';
import { useGetHatvKPILegListQuery } from '../../../redux/services/hatvKPIAPI';
import { useLazyGetHATVQmonthDetailsAPIQuery } from '../../../redux/services/hatvQmonthDetailsAPI';

interface TableRow {
  label: string;
  values: ValueType[];
}
interface ValueType {
  closingMonth: string;
  kpiValue: string | number | null | ReactNode;
  kpiFlagCode?: number | null;
  kpiFlagName?: string | null;
}

const LegViolation = ({
  closingMonth,
  affiliateCode,
  aboNumber,
}: {
  closingMonth: string;
  affiliateCode: string;
  aboNumber: number;
}) => {
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { t } = useTranslation();
  const isExpanded = useAppSelector((state) => state.sideNavExpansion.isExpanded);
  const [isAPIError, setIsAPIError] = useState(false);
  const [qvABOName, setQvABOName] = useState<string>('');
  const [selectedAboNumber, setSelectedAboNumber] = useState<string | null>(null);
  const [selectedAffiliateCode, setSelectedAffiliateCode] = useState<string | null>(null);
  const [selectedClosingMonth, setSelectedClosingMonth] = useState<string | null>(null);
  const [isApprovalStatus, setIsApprovalStatus] = useState<Number | null>(null);
  const [hatvkpiQvUsed, setHatvkpiQvUsed] = useState<Record<string, any[]>>({});
  const [qTypeSelected, setQtypeSelected] = useState<string>('');
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const handleABOInfoClick = useABOProfileNavigation();
  const {
    data: kpiListData,
    isLoading: isLoadingKPIList,
    isFetching: isFetchingKPIList,
    isError: isKPIListError,
    isSuccess: isKPIListSuccess,
    refetch: refetchKPIList,
  } = useGetHatvKPILegListQuery(
    {
      affiliateCode,
      aboNumber,
      closingMonth,
    },
    {
      skip: !closingMonth || !affiliateCode || !aboNumber,
    }
  );
  const [
    triggerQDetail,
    {
      data: qDetail,
      isLoading: isLoadingQDetail,
      isFetching: isFetchingQDetail,
      isError: isQDetailError,
      isSuccess: isQDetailSuccess,
    },
  ] = useLazyGetHATVQmonthDetailsAPIQuery();

  const tooltipDataCache = useRef<
    Map<string, { qType: string; approvalStatus: number | null; kpis: Record<string, any> }>
  >(new Map());

  const fetchedTooltipKeys = useRef<Set<string>>(new Set());

  const mapRowKeyToAPIKey: Record<string, string> = {
    hatvGroupSize: 'hatvGroupSize',
    hatvContributorCount: 'hatvContributorCount',
    highestPpvInGroup: 'highestPpvInGroup',
    volumeFluctuation: 'volumeFluctuation',
    tenure: 'tenure',
    bcrCase: 'bcrCase',
    eomGroupPv: 'eomGpv',
  };

  useEffect(() => {
    const list = qDetail?.body?.qDetail;
    if (!Array.isArray(list) || list.length === 0) return;

    const detail =
      list.find(
        (d: any) =>
          (!selectedAboNumber || d?.qvUsed?.aboNumber === selectedAboNumber) &&
          (!selectedAffiliateCode || d?.qvUsed?.affiliateCode === selectedAffiliateCode)
      ) || list[0];

    const approvalStatus = detail?.qMonthApproval?.approvalStatus ?? null;
    const qualificationType = detail?.qType ?? '';

    const rawKpis = detail?.hatvKpis ?? {};
    const normalizedForTooltip: Record<string, any> = {};
    Object.entries(mapRowKeyToAPIKey).forEach(([rowKey, apiKey]) => {
      if (rawKpis && rawKpis[apiKey] != null) {
        normalizedForTooltip[rowKey] = rawKpis[apiKey];
      }
    });

    // Set states
    setQtypeSelected(qualificationType);
    setIsApprovalStatus(approvalStatus);
    setHatvkpiQvUsed(normalizedForTooltip);
    // Set ABO name from qDetail response (for aboFlagged which doesn't include name)
    setQvABOName(detail?.qvUsed?.name || '');

    // Save to cache
    if (selectedAboNumber && selectedAffiliateCode && selectedClosingMonth) {
      const cacheKey = `${selectedAffiliateCode}-${selectedAboNumber}-${selectedClosingMonth}`;
      tooltipDataCache.current.set(cacheKey, {
        qType: qualificationType,
        approvalStatus,
        kpis: normalizedForTooltip,
      });
    }
  }, [qDetail, selectedAboNumber, selectedAffiliateCode, selectedClosingMonth]);

  useEffect(() => {
    const hasNoData = !kpiListData?.body?.qMonthApproval?.length;
    if (isKPIListSuccess && hasNoData) {
      setIsAPIError(true);
    }
  }, [isKPIListSuccess, kpiListData]);

  useEffect(() => {
    setIsAPIError(false);

    // Clear tooltip state
    setSelectedRowKey(null);
    setSelectedAboNumber(null);
    setSelectedAffiliateCode(null);
    setSelectedClosingMonth(null);
    setQtypeSelected('');
    setIsApprovalStatus(null);
    setHatvkpiQvUsed({});

    // Clear cache
    fetchedTooltipKeys.current = new Set();
    tooltipDataCache.current = new Map();
  }, [closingMonth, affiliateCode, aboNumber]);

  const handleFlag = (
    clickedClosingMonth: string,
    qualificationDetails: any,
    rowKey: string,
    aboFlagged?: { affiliateCode?: string; aboNumber?: string; name?: string }
  ) => {
    // Use aboFlagged if provided (for leg details), otherwise fall back to qvUsed
    let qvAboNumber: string | undefined;
    let qaABOName: string | undefined;
    let qvAffiliateCode: string | undefined;

    if (aboFlagged?.aboNumber) {
      qvAboNumber = aboFlagged.aboNumber;

      qvAffiliateCode = aboFlagged.affiliateCode ?? affiliateCode;
    } else {
      const match = qualificationDetails.find(
        (qv: any) => qv.closingMonth === clickedClosingMonth && qv?.qvUsed?.aboNumber
      );
      qvAboNumber = match?.qvUsed?.aboNumber;
      qaABOName = match?.qvUsed?.name;
      qvAffiliateCode = match?.qvUsed?.affiliateCode ?? affiliateCode;
    }

    if (qvAboNumber && clickedClosingMonth && qvAffiliateCode) {
      setSelectedAboNumber(qvAboNumber);
      setSelectedClosingMonth(clickedClosingMonth);
      setQvABOName(qaABOName || '');
      setSelectedAffiliateCode(qvAffiliateCode ?? null);
      setSelectedRowKey(rowKey);

      const cacheKey = `${qvAffiliateCode}-${qvAboNumber}-${clickedClosingMonth}`;

      // If we have data in cache - set state and return
      const cached = tooltipDataCache.current.get(cacheKey);
      if (cached) {
        setQtypeSelected(cached.qType);
        setIsApprovalStatus(cached.approvalStatus);
        setHatvkpiQvUsed(cached.kpis);
        return;
      }

      // If not - prevent duplicate requests
      if (!fetchedTooltipKeys.current.has(cacheKey)) {
        triggerQDetail({
          affiliateCode: qvAffiliateCode as string,
          aboNumber: qvAboNumber,
          closingMonth: clickedClosingMonth,
        });
        fetchedTooltipKeys.current.add(cacheKey);
      }
    } else {
      console.warn('No valid ABO found. Skipping API trigger.');
      setHatvkpiQvUsed({});
      setIsApprovalStatus(null);
    }
  };

  const approvalStatusText =
    typeof isApprovalStatus === 'number' ? t(`q_month_approval_status.${isApprovalStatus}`) : '';

  const getFlagColor = (flagCode: number, theme: string) => {
    if (flagCode === 3) {
      return theme === 'light' ? '#D91734' : '#F16A81';
    }
    if (flagCode === 2) {
      return theme === 'light' ? '#DA7600' : '#FED773';
    }
    return '';
  };
  const getTooltipContent = (
    flagCode: number,
    qvABOName: string,
    qvAboNumber: string | null,
    qvAffiliateCode: string | null,
    approvalStatusText: string | null,
    hatvkpiQvUsed: any,
    rowKey: string,
    qType: string
  ) => {
    const rawValue = hatvkpiQvUsed?.[selectedRowKey ?? rowKey];
    let matchingKPI = null;
    if (Array.isArray(rawValue)) {
      matchingKPI = rawValue.find((kpi: any) => kpi.kpiFlagCode === flagCode);
    } else if (rawValue && typeof rawValue === 'object') {
      matchingKPI = rawValue;
    }

    const specificFlagCode: number | null = matchingKPI?.kpiFlagCode ?? null;

    return (
      <div className={`tooltip_content_wrapper ${styles.tooltip_content_wrapper}`}>
        <div className={styles.flagged_qualifier_wrapepr}>
          <span className={styles.flagged_qualifier_title}>{t('flagged_qualifier')}:</span>
          <div className={styles.abo_qualifier_details}>
            <span
              className={styles.abo_qualifier_name}
              onClick={() =>
                handleABOInfoClick(qvAboNumber ?? undefined, qvAffiliateCode ?? affiliateCode)
              }
            >
              {qvABOName && `${qvABOName} `}
            </span>
            <span className={styles.abo_qualifier_no}>#{qvAboNumber ?? '-'}</span>
          </div>
        </div>
        <div className={styles.qualifiction_wrapepr}>
          <span className={styles.qualification_title}>{t('qualification_type')}:</span>
          <div className={styles.qualification_status}>
            <span className={styles.abo_qualifier_name}>{qTypeSelected}</span>
            <div className={`qualifier_flag_wrapper ${styles.qualifier_flag_wrapper}`}>
              <span
                className={`${styles.qualifier_flag} ${specificFlagCode === null && styles.hide_flag}`}
              >
                {typeof specificFlagCode === 'number' && (
                  <Icon
                    name="flag"
                    size={14}
                    color={getFlagColor(specificFlagCode, currentTheme)}
                    className={styles.flag}
                  />
                )}
              </span>
              <span
                className={`qualifier_text ${styles.qualifier_text} ${
                  specificFlagCode === 3
                    ? 'red_text'
                    : specificFlagCode === 2
                      ? 'yellow_text'
                      : 'default'
                }`}
              >
                {' '}
                {specificFlagCode === 3 ? 'Red ' : specificFlagCode === 2 ? 'Yellow ' : ''}
                {approvalStatusText}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getIconWithTooltip = (item: any, qualificationDetails: any, rowKey: string) => {
    const isRed = item.kpiFlagCode === 3;
    const flagCode = isRed ? 3 : 2;
    const color = isRed
      ? currentTheme === 'light'
        ? '#D91734'
        : '#F16A81'
      : currentTheme === 'light'
        ? '#DA7600'
        : '#FED773';

    const match = qualificationDetails.find(
      (qv: any) =>
        (qv.qType === 'Q1' || qv.qType === 'Q2' || qv.qType === 'QV') &&
        qv.closingMonth === item.closingMonth
    );
    // Use aboFlagged from the KPI item for leg details (cross-level downline)
    const aboFlagged = item.aboFlagged;
    const qvABONumber = aboFlagged?.aboNumber;
    const qvAffiliateCode = aboFlagged?.affiliateCode;
    const qType = match?.qType;
    const handleClick = () => {
      if (qvABONumber && qvAffiliateCode) {
        handleFlag(item.closingMonth, qualificationDetails, rowKey, aboFlagged);
      }
    };

    return (
      <div className={`leg_tooltip ${styles.leg_tooltip}`}>
        <Tooltip
          content={
            isFetchingQDetail ? (
              <>Loader...</>
            ) : selectedRowKey && hatvkpiQvUsed[selectedRowKey] ? (
              getTooltipContent(
                flagCode,
                qvABOName,
                qvABONumber ?? null,
                qvAffiliateCode,
                approvalStatusText,
                hatvkpiQvUsed,
                rowKey,
                qType
              )
            ) : (
              <>{t('dropdown_items_not_available')}</>
            )
          }
          width="288px"
          height="auto"
          bottom="33px"
          tipPos={true}
          hideHover={true}
        >
          <Icon name="flag" size={14} color={color} className={styles.flag} onClick={handleClick} />
        </Tooltip>
      </div>
    );
  };

  const getTableRowsFromKPIListData = (): TableRow[] => {
    if (!kpiListData?.body) return [];

    const { qMonthApproval, hatvKpis, qualificationDetails } = kpiListData.body;

    const standardKPIFields: { label: string; key: keyof typeof hatvKpis; rowKey: string }[] = [
      { label: 'hatvGroupSize', key: 'groupSize', rowKey: 'hatvGroupSize' },
      { label: 'hatvContributorCount', key: 'contributorCount', rowKey: 'hatvContributorCount' },
      { label: 'highestPpvInGroup', key: 'highestPpvInGroup', rowKey: 'highestPpvInGroup' },
      { label: 'volumeFluctuation', key: 'volumeFluctuation', rowKey: 'volumeFluctuation' },
      { label: 'eomGpv', key: 'eomGroupPv', rowKey: 'eomGroupPv' },
      { label: 'tenure', key: 'tenure', rowKey: 'tenure' },
      { label: 'bcrCase', key: 'bcrCase', rowKey: 'bcrCase' },
    ];

    const getApprovalLabel = (status: number) => {
      return typeof status === 'number' ? t(`q_month_approval_status.${status}`) || '-' : '-';
    };

    const rows: TableRow[] = [
      {
        label: 'approvedStatus',
        values: qMonthApproval?.map((item: any) => ({
          kpiValue: getApprovalLabel(item.approvalStatus),
          kpiFlagCode: null,
          kpiFlagName: null,
          closingMonth: item.closingMonth,
        })),
      },
      ...standardKPIFields?.map(({ label, key, rowKey }) => ({
        label,
        rowKey,
        values: hatvKpis[key]?.map((item: any) => {
          let kpiValue;

          if (key === 'tenure' || key === 'bcrCase') {
            kpiValue =
              item.kpiFlagCode === 1 ? (
                t('no')
              ) : item.kpiFlagCode === 3 ? (
                <Icon
                  name="flag"
                  size={14}
                  color={currentTheme === 'light' ? '#D91734' : '#F16A81'}
                  className={styles.flag}
                />
              ) : (
                '-'
              );
          } else {
            kpiValue =
              item.kpiFlagCode === 1 ? (
                <Icon
                  name="checked_icon"
                  size={14}
                  color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                  className={styles.flag}
                />
              ) : item.kpiFlagCode === 2 || item.kpiFlagCode === 3 ? (
                getIconWithTooltip(item, qualificationDetails, rowKey)
              ) : item.kpiValue ? (
                `${item.kpiValue}`
              ) : (
                '-'
              );
          }

          return {
            ...item,
            kpiValue,
            closingMonth: item.closingMonth,
          };
        }),
      })),
    ];

    return rows;
  };

  const tableRows = getTableRowsFromKPIListData();
  if (isLoadingKPIList || isFetchingKPIList) {
    return <MyAccountsLoader num={7} hideCustomViewPanel={true} />;
  }

  if (isKPIListError || isAPIError || !kpiListData?.body?.qMonthApproval?.length) {
    return (
      <ErrorInPageScreen
        handleClickTryAgain={() => {
          setIsAPIError(false);
          refetchKPIList();
        }}
      />
    );
  }
  return (
    <div className={`leg_violation_wrapper ${styles.leg_violation_wrapper}`}>
      <div className={`table_outer_wrapper ${styles.table_outer_wrapper}`}>
        <table className={styles.table_wrapper}>
          <thead>
            <tr className={`tbl_header ${styles.tbl_header}`}>
              <th></th>
              {tableRows[0]?.values?.map((val, index) => (
                <th key={index}>
                  {t(`hatv_months.${getCustomShortMonthLabel(val.closingMonth?.toString())}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`tbl_body ${styles.tbl_body}`}>
            {tableRows?.map((row, index) => (
              <tr
                key={index}
                className={`tbl_row ${styles.tbl_row} ${
                  index % 2 === 0 ? `even_row ${styles.even_row}` : `odd_row ${styles.odd_row}`
                }`}
              >
                <td>{t(`violation_table.labels.${row.label}`)}</td>
                {row &&
                  row?.values?.map((val, i) => (
                    <td key={i}>
                      {val?.kpiFlagName ? (
                        <div className={styles.flag_wrapper}>
                          {val?.kpiValue !== null && (
                            <span className={`flag_name ${styles.flag_name} `}>{val.kpiValue}</span>
                          )}
                        </div>
                      ) : val?.kpiValue !== null ? (
                        val.kpiValue === 'checked' ? (
                          <div className={styles.checked_icon_wrapper}>
                            <Icon
                              name="checked_icon"
                              size={14}
                              color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                              className={styles.checked_icon}
                            />
                          </div>
                        ) : (
                          <span
                            className={
                              val.kpiValue === 'Denied' ? `deniend_text ${styles.deniend_text}` : ''
                            }
                          >
                            {val.kpiValue}
                          </span>
                        )
                      ) : (
                        <div className={styles.hyper_arrow_wrapper}>
                          <Icon
                            name="hypen_arrow"
                            size={9}
                            color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                            className={styles.hypen_arrow}
                          />
                        </div>
                      )}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LegViolation;
